/**
 * CiteAndPrint — small bar with two SEO-friendly actions:
 *   1. "Cite this page" — copies APA + BibTeX to clipboard. Critical for
 *      medical bloggers and journalists linking back to TCP.
 *   2. "Print / Save as PDF" — invokes browser print, which on every modern
 *      browser produces a savable PDF. Parents share these in WhatsApp /
 *      Telegram groups → organic distribution channel.
 *
 * Reusable across explorers — pass any title + canonicalPath.
 */
import React, { useState } from "react";
import { Quote, Printer, Check, Copy } from "lucide-react";

function formatApa({ title, url, accessed }) {
    return `The Context Project. (${accessed.slice(0, 4)}). ${title}. The Context Project Library. Retrieved ${accessed} from ${url}`;
}

function formatBibtex({ title, url, accessed, key }) {
    return `@misc{${key},
  author = {{The Context Project}},
  title = {${title}},
  year = {${accessed.slice(0, 4)}},
  url = {${url}},
  urldate = {${accessed}}
}`;
}

export default function CiteAndPrint({ title, canonicalPath, slug, className = "" }) {
    const [copied, setCopied] = useState(null);  // "apa" | "bibtex" | null
    const [open, setOpen] = useState(false);

    const url = `https://library.thecontextproject.com${canonicalPath}`;
    const accessed = new Date().toISOString().slice(0, 10);
    const apa = formatApa({ title, url, accessed });
    const bibtex = formatBibtex({ title, url, accessed, key: `tcp-${slug || canonicalPath.replace(/\W+/g, "-").replace(/^-|-$/g, "")}` });

    const copy = async (text, kind) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(kind);
            setTimeout(() => setCopied(null), 1500);
        } catch { /* ignore */ }
    };

    return (
        <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="cite-and-print">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="cite-button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium tracking-wide bg-[#111827] border border-[#243044] hover:border-[#56CCF2]/50 text-[#CBD5E1] hover:text-white transition-colors"
            >
                <Quote className="w-3 h-3" />
                Cite this page
            </button>
            <button
                type="button"
                onClick={() => window.print()}
                data-testid="print-button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium tracking-wide bg-[#111827] border border-[#243044] hover:border-[#56CCF2]/50 text-[#CBD5E1] hover:text-white transition-colors"
            >
                <Printer className="w-3 h-3" />
                Print / Save as PDF
            </button>
            {open ? (
                <div className="basis-full mt-3 bg-[#0B1220] border border-[#243044] rounded-lg p-4 space-y-4" data-testid="cite-panel">
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] font-semibold">APA</span>
                            <button onClick={() => copy(apa, "apa")} data-testid="copy-apa" className="text-[10px] text-[#94A3B8] hover:text-white inline-flex items-center gap-1">
                                {copied === "apa" ? <><Check className="w-3 h-3 text-[#7FE0A6]" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                            </button>
                        </div>
                        <pre className="text-[12px] text-[#CBD5E1] bg-[#111827] border border-[#243044] rounded p-2.5 overflow-x-auto whitespace-pre-wrap">{apa}</pre>
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] font-semibold">BibTeX</span>
                            <button onClick={() => copy(bibtex, "bibtex")} data-testid="copy-bibtex" className="text-[10px] text-[#94A3B8] hover:text-white inline-flex items-center gap-1">
                                {copied === "bibtex" ? <><Check className="w-3 h-3 text-[#7FE0A6]" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                            </button>
                        </div>
                        <pre className="text-[12px] text-[#CBD5E1] bg-[#111827] border border-[#243044] rounded p-2.5 overflow-x-auto whitespace-pre font-mono">{bibtex}</pre>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
